import {
    BoxIcon,
    CalendarIcon,
    CircleCrossIcon,
    EmailIcon,
    EquipmentIcon,
    LinkIcon,
    MoneyAddIcon,
    MoneyBackIcon,
    PrintIcon,
    RefreshIcon,
    RosterIcon,
    SendIcon,
    UserAddIcon,
} from "@xola/icons";
import { canCancel, isEquipmentItem, PurchaseActionOption } from "common";
import React from "react";
import { ExperiencePurchaseItem, Guest, Permission, Purchase } from "types";
import { IsGranted } from "@/components/Common/IsGranted";
import { Menu } from "@/components/Common/Menu";
import { isPackageItem } from "@/models/Purchase";
import { canViewWaivers } from "@/stores/globalStore";

interface ExperienceMenuItemsProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    isOrganizer: boolean;
    guest?: Guest;
    canSendEmail: boolean;
    shouldSendSms: boolean;
    isSendingConfirmationEmail: boolean;
    isSendingConfirmationSms: boolean;
    isConfirmationEmailSent: boolean;
    isConfirmationSmsSent: boolean;
    onButtonClick: (action: PurchaseActionOption) => void;
    onConfirmationMessage: () => void;
}

export const ExperienceMenuItems = ({
    purchase,
    item,
    isOrganizer,
    guest,
    canSendEmail,
    shouldSendSms,
    isSendingConfirmationEmail,
    isSendingConfirmationSms,
    isConfirmationEmailSent,
    isConfirmationSmsSent,
    onButtonClick,
    onConfirmationMessage,
}: ExperienceMenuItemsProps) => {
    return (
        <>
            <Menu.Item icon={<SendIcon />} onClick={async () => onButtonClick(PurchaseActionOption.MESSAGE)}>
                Message
            </Menu.Item>

            {isOrganizer && (
                <Menu.Item
                    icon={<RosterIcon />}
                    onClick={async () => onButtonClick(PurchaseActionOption.ADD_PURCHASE_NOTE)}
                >
                    Add Purchase Note
                </Menu.Item>
            )}

            <Menu.Item
                isDisabled={!canSendEmail && !shouldSendSms}
                isLoading={isSendingConfirmationEmail || isSendingConfirmationSms}
                isSuccess={isConfirmationEmailSent && isConfirmationSmsSent}
                icon={<EmailIcon />}
                onClick={onConfirmationMessage}
            >
                Resend Confirmation
            </Menu.Item>

            <Menu.Item icon={<CalendarIcon />} onClick={async () => onButtonClick(PurchaseActionOption.CHANGE_ARRIVAL)}>
                Change Arrival
            </Menu.Item>

            <Menu.Item
                icon={isEquipmentItem(item) ? <EquipmentIcon /> : <UserAddIcon />}
                onClick={async () => onButtonClick(PurchaseActionOption.MODIFY_GUESTS)}
            >
                {isEquipmentItem(item) ? "Modify Equipment" : "Modify Guests"}
            </Menu.Item>

            <Menu.Item icon={<BoxIcon />} onClick={async () => onButtonClick(PurchaseActionOption.EDIT_ADD_ONS)}>
                Modify Add-ons
            </Menu.Item>

            <Menu.Item icon={<MoneyAddIcon />} onClick={async () => onButtonClick(PurchaseActionOption.CHARGE)}>
                Charge
            </Menu.Item>

            {purchase.amount > 0 && (
                <IsGranted permissions={Permission.REFUND_AMOUNT}>
                    <Menu.Item
                        icon={<MoneyBackIcon />}
                        onClick={async () => onButtonClick(PurchaseActionOption.REFUND)}
                    >
                        Refund
                    </Menu.Item>
                </IsGranted>
            )}

            {!isPackageItem(item) && !isEquipmentItem(item) && (
                <Menu.Item
                    icon={<RefreshIcon />}
                    onClick={async () => onButtonClick(PurchaseActionOption.SWITCH_EXPERIENCE)}
                >
                    Switch Experience
                </Menu.Item>
            )}

            <IsGranted permissions={Permission.MODIFY_BOOKING_LITE}>
                {canCancel(item) ? (
                    <Menu.Item
                        icon={<CircleCrossIcon />}
                        onClick={async () => onButtonClick(PurchaseActionOption.CANCEL_RESERVATION)}
                    >
                        Cancel Reservation
                    </Menu.Item>
                ) : null}
            </IsGranted>

            {canViewWaivers() && canCancel(item) ? (
                <Menu.Item
                    icon={<LinkIcon />}
                    onClick={async () => {
                        const actionOption =
                            isOrganizer && !guest
                                ? PurchaseActionOption.LINK_WAIVER_FOR_PURCHASE
                                : PurchaseActionOption.LINK_WAIVER_FOR_GUEST;

                        onButtonClick(actionOption);
                    }}
                >
                    Link Waiver
                </Menu.Item>
            ) : null}

            <IsGranted permissions={Permission.PRINT_RECEIPT}>
                <Menu.Item icon={<PrintIcon />} onClick={async () => onButtonClick(PurchaseActionOption.PRINT_RECEIPT)}>
                    Print Receipt
                </Menu.Item>
            </IsGranted>
        </>
    );
};
